import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toPascal, toCamel } from './utils';

function swiftType(t: string): string {
  return t === 'number' ? 'Double' : t === 'boolean' ? 'Bool' : t === 'array' ? '[Any]' : t === 'object' ? '[String: Any]' : 'String';
}

function genConfig(config: ExportConfig): string {
  const appCases = config.apps
    .map((a) => `    case ${a.key} = "${a.key}"`)
    .join("\n");

  const fromEmailSwitch = config.apps
    .map((a) => `        case .${a.key}: return "${a.fromEmail}"`)
    .join("\n");

  const keyEnvSwitch = config.apps
    .map((a) => `        case .${a.key}: return "${a.envVarName}"`)
    .join("\n");

  const templateCases = config.apps
    .flatMap((a) => a.templates.map((t) => `    case ${t.camelName} = "${t.slug}"`))
    .join("\n");

  const templateAppSwitch = config.apps
    .flatMap((a) =>
      a.templates.map((t) => `        case .${t.camelName}: return .${a.key}`)
    )
    .join("\n");

  return `// MailConfig.swift — generated by Mail Service SDK Export

enum MailApp: String {
${appCases}

    var fromEmail: String {
        switch self {
${fromEmailSwitch}
        }
    }

    var keyEnv: String {
        switch self {
${keyEnvSwitch}
        }
    }
}

enum MailTemplate: String {
${templateCases}

    var app: MailApp {
        switch self {
${templateAppSwitch}
        }
    }
}
`;
}

function genService(config: ExportConfig): string {
  const typedMethods = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const fnName = `send${toPascal(t.camelName)}`;
        if (t.schema && t.schema.length > 0) {
          const params = t.schema
            .map((f) => `${toCamel(f.key)}: ${swiftType(f.type)}${f.required ? '' : '?'}`)
            .join(', ');
          const dictEntries = t.schema
            .map((f) => `"${f.key}": ${toCamel(f.key)}${f.required ? '' : ' as Any'}`)
            .join(', ');
          return `    func ${fnName}(recipient: String, ${params}) async throws -> [String: Any] {\n        return try await send(.${t.camelName}, recipient: recipient, data: [${dictEntries}])\n    }`;
        }
        return `    func ${fnName}(recipient: String, data: [String: Any] = [:]) async throws -> [String: Any] {\n        return try await send(.${t.camelName}, recipient: recipient, data: data)\n    }`;
      }),
    )
    .join("\n\n");

  return `// MailService.swift — generated by Mail Service SDK Export
import Foundation

class MailService {
    private let serviceURL: String

    init() {
        self.serviceURL = (ProcessInfo.processInfo.environment["${config.serviceUrlVarName}"] ?? "")
            .trimmingCharacters(in: CharacterSet(charactersIn: "/").union(.whitespaces))
    }

    private func getKey(_ app: MailApp) -> String {
        ProcessInfo.processInfo.environment[app.keyEnv] ?? ""
    }

    private func post(_ app: MailApp, path: String, body: [String: Any]) async throws -> [String: Any] {
        guard !serviceURL.isEmpty, !getKey(app).isEmpty else {
            return ["success": false, "error": "not configured"]
        }
        guard let url = URL(string: serviceURL + path) else { throw URLError(.badURL) }
        var req = URLRequest(url: url)
        req.httpMethod = "POST"
        req.setValue("application/json", forHTTPHeaderField: "Content-Type")
        req.setValue(getKey(app), forHTTPHeaderField: "X-API-KEY")
        req.httpBody = try JSONSerialization.data(withJSONObject: body)
        let (data, _) = try await URLSession.shared.data(for: req)
        return (try JSONSerialization.jsonObject(with: data) as? [String: Any]) ?? ["success": false]
    }

    func send(_ template: MailTemplate, recipient: String, data: [String: Any] = [:]) async throws -> [String: Any] {
        return try await post(template.app, path: "/v1/send", body: [
            "template_slug": template.rawValue,
            "recipient": recipient,
            "data": data,
        ])
    }

${typedMethods}
}
`;
}

export const swiftGenerator: LanguageGenerator = {
  id: 'swift',
  name: 'Swift',
  category: 'full-sdk',
  color: '#F05138',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'MailConfig.swift', content: genConfig(config), syntax: 'swift' },
      { filename: 'MailService.swift', content: genService(config), syntax: 'swift' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
